import { useState, useMemo, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { 
  Bell, 
  ChevronDown,
  ChevronRight,
  Menu, 
  Home,
  User,
  Settings,
  LogOut,
  Building2,
  CheckCircle2,
  Sparkles,
  Shield,
  MoreHorizontal,
  ArrowRight,
  Brain,
  Layers
} from 'lucide-react'
import { clsx } from 'clsx'
import { clientNavigation, vendorNavigation, superAdminNavigation } from '../config/navigationConfig'
import { useAuth } from '../contexts/AuthContext'
import RealTimeNotificationCenter from './RealTimeNotificationCenter'
import SignOutModal from './SignOutModal'
import BrandLogo from './BrandLogo'
import SupportSessionBanner from './SupportSessionBanner'

// Intelligent Hierarchical Dropdown Component for Main Menu Items
const HierarchicalNavDropdown = ({ item, isOpen, onClose }) => {
  const [activeSubIndex, setActiveSubIndex] = useState(0)
  const menuRef = useRef(null)
  const [alignRight, setAlignRight] = useState(false)

  // Reset active sub-item when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setActiveSubIndex(0)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const el = menuRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth || document.documentElement.clientWidth
    setAlignRight(rect.right > vw - 15)
  }, [isOpen])

  if (!isOpen || !item.children || item.children.length === 0) return null

  // Check if this suite has 2-level hierarchy (categories with children)
  const hasSubCategories = item.children.some(c => c.children && c.children.length > 0)
  const currentCategory = hasSubCategories ? (item.children[activeSubIndex] || item.children[0]) : null

  return (
    <div 
      ref={menuRef}
      className={clsx(
        "absolute top-full mt-1 bg-white border border-slate-200/95 shadow-2xl rounded-2xl z-[99999] overflow-hidden animate-in fade-in zoom-in-95 duration-150 text-slate-800",
        alignRight ? "right-0" : "left-0",
        hasSubCategories ? "min-w-[560px] max-w-[680px]" : "min-w-[320px] max-w-[380px]"
      )}
    >
      {/* Dropdown Header */}
      <div className="px-4 py-2.5 bg-gradient-to-r from-slate-900 to-slate-800 text-white flex items-center justify-between border-b border-slate-700/60">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-blue-600/80 flex items-center justify-center text-white">
            {item.icon ? <item.icon className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
          </div>
          <div>
            <span className="text-xs font-bold tracking-wide text-slate-100">{item.headerTitle || item.name}</span>
            <span className="text-[10px] text-blue-300 ml-2 font-medium">Suite</span>
          </div>
        </div>
        {item.href && item.href !== '#' && (
          <Link
            to={item.href}
            onClick={onClose}
            className="flex items-center gap-1 text-[11px] font-semibold text-blue-300 hover:text-white transition-colors"
          >
            <span>Open Suite</span>
            <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>

      {/* Dropdown Body: 2-Level Hierarchy or Single-Level List */}
      {hasSubCategories ? (
        <div className="flex min-h-[260px] divide-x divide-slate-100">
          
          {/* Level 1: Submenu Categories (Hover to reveal nested submenus) */}
          <div className="w-[230px] p-2 bg-slate-50/70 flex flex-col space-y-1 shrink-0">
            <div className="px-2.5 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Submenus
            </div>
            {item.children.map((sub, idx) => {
              const isSelected = activeSubIndex === idx
              const subHasChildren = sub.children && sub.children.length > 0
              
              return (
                <div
                  key={`${sub.name}-${idx}`}
                  onMouseEnter={() => setActiveSubIndex(idx)}
                  onMouseOver={() => setActiveSubIndex(idx)}
                  onClick={() => setActiveSubIndex(idx)}
                  className={clsx(
                    "group flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium cursor-pointer transition-all duration-150 select-none",
                    isSelected
                      ? "bg-white text-blue-700 font-bold shadow-sm ring-1 ring-slate-200/80"
                      : "text-slate-700 hover:bg-slate-200/60 hover:text-slate-900"
                  )}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    {sub.icon ? (
                      <sub.icon className={clsx(
                        "w-4 h-4 shrink-0 transition-colors",
                        isSelected ? "text-blue-600" : "text-slate-400 group-hover:text-slate-600"
                      )} />
                    ) : (
                      <Layers className="w-4 h-4 text-slate-400" />
                    )}
                    <div className="flex flex-col min-w-0">
                      <span className="truncate">{sub.name}</span>
                    </div>
                  </div>
                  {subHasChildren && (
                    <ChevronRight className={clsx(
                      "w-3.5 h-3.5 transition-transform duration-150 shrink-0",
                      isSelected ? "text-blue-600 translate-x-0.5" : "text-slate-300 group-hover:text-slate-400"
                    )} />
                  )}
                </div>
              )
            })}
          </div>

          {/* Level 2: Nested Submenu Items for Currently Hovered Category */}
          <div className="flex-1 p-3 bg-white flex flex-col min-w-0">
            {currentCategory && currentCategory.children && currentCategory.children.length > 0 ? (
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    {currentCategory.icon && <currentCategory.icon className="w-3.5 h-3.5 text-blue-600" />}
                    <span className="text-xs font-bold text-slate-900">{currentCategory.name}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-medium">
                    {currentCategory.children.length} features
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-1 overflow-y-auto max-h-[300px] pr-1 stylish-scrollbar">
                  {currentCategory.children.map((child, cIdx) => (
                    <Link
                      key={`${child.name}-${cIdx}`}
                      to={child.href}
                      onClick={onClose}
                      className="group flex items-start gap-3 p-2 rounded-xl hover:bg-blue-50/90 transition-all text-left"
                    >
                      <div className="w-6 h-6 rounded-lg bg-slate-100 group-hover:bg-blue-100/80 text-slate-500 group-hover:text-blue-600 flex items-center justify-center shrink-0 mt-0.5 transition-colors">
                        {child.icon ? <child.icon className="w-3.5 h-3.5" /> : <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-xs font-semibold text-slate-800 group-hover:text-blue-700 transition-colors">
                          {child.name}
                        </span>
                        {child.description && (
                          <span className="text-[10px] text-slate-400 group-hover:text-slate-600 line-clamp-1 mt-0.5 transition-colors">
                            {child.description}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-slate-400">
                Select a submenu category on the left to view features.
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Single-Level Direct Actions List */
        <div className="p-2 space-y-1 max-h-[320px] overflow-y-auto stylish-scrollbar">
          {item.children.map((child, idx) => (
            <Link
              key={`${child.name}-${idx}`}
              to={child.href}
              onClick={onClose}
              className="group flex items-start gap-3 p-2.5 rounded-xl hover:bg-blue-50/90 transition-all text-left"
            >
              <div className="w-7 h-7 rounded-lg bg-slate-100 group-hover:bg-blue-100/80 text-slate-600 group-hover:text-blue-600 flex items-center justify-center shrink-0 mt-0.5 transition-colors">
                {child.icon ? <child.icon className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-xs font-semibold text-slate-800 group-hover:text-blue-700 transition-colors">
                  {child.name}
                </span>
                {child.description && (
                  <span className="text-[10px] text-slate-400 group-hover:text-slate-600 line-clamp-2 mt-0.5 transition-colors">
                    {child.description}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default function TopNavigation({ onMobileMenuOpen, fullWidth = false }) {
  const { 
    user, 
    isSuperAdmin, 
    activePortal, 
    switchPortal, 
    logout, 
    hasAnyPermission, 
    enabledModules, 
    licenseStatus,
    supportSessionTenant,
    endSupportSession
  } = useAuth()
  const location = useLocation()
  
  const [hoveredMenu, setHoveredMenu] = useState(null)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [showSignOutModal, setShowSignOutModal] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const navContainerRef = useRef(null)
  const userMenuRef = useRef(null)
  const leaveTimeoutRef = useRef(null)

  const confirmSignOut = async () => {
    try {
      setIsSigningOut(true)
      await logout()
    } finally {
      setIsSigningOut(false)
      setShowSignOutModal(false)
    }
  }

  // Close menus on route change
  useEffect(() => {
    setHoveredMenu(null)
    setUserMenuOpen(false)
  }, [location.pathname])

  // Close menus on outside click or Escape
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (navContainerRef.current && !navContainerRef.current.contains(e.target)) {
        setHoveredMenu(null)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false)
      }
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setHoveredMenu(null)
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleGlobalClick)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleGlobalClick)
      document.removeEventListener('keydown', handleKeyDown)
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    }
  }, [])

  // Select navigation based on active portal context and SuperAdmin separation
  const activeNavigation = useMemo(() => {
    if (isSuperAdmin || user?.type === 'SuperAdmin' || activePortal === 'superadmin' || location.pathname.startsWith('/superadmin')) {
      return superAdminNavigation
    }
    if (activePortal === 'vendor' || user?.type === 'Vendor' || location.pathname.startsWith('/vendor')) {
      return vendorNavigation
    }
    return clientNavigation
  }, [activePortal, isSuperAdmin, user?.type, location.pathname])

  // Filter navigation based on permissions and enabled modules
  const filteredNavigation = useMemo(() => {
    if (isSuperAdmin || user?.type === 'SuperAdmin' || activePortal === 'superadmin' || location.pathname.startsWith('/superadmin')) {
      return superAdminNavigation
    }
    
    const aliasMap = {
      'procurement': ['operations', 'procurement', 'buy', 'po'],
      'sourcing': ['operations', 'sourcing', 'source'],
      'operations': ['operations', 'procurement'],
      'approvals': ['approval_workflow', 'approvals'],
      'finance': ['finance'],
      'budget': ['budget'],
      'pos': ['pos'],
      'grc': ['grc'],
      'inventory': ['inventory'],
      'platform': ['platform', 'settings'],
      'settings': ['platform', 'settings'],
      'dashboard': ['analytics_ai', 'dashboard'],
      'reporting': ['reporting', 'reports', 'platform']
    };

    const checkModuleEntitled = (moduleCode) => {
      if (!moduleCode || isSuperAdmin) return true;
      const lower = String(moduleCode).toLowerCase();
      const candidates = aliasMap[lower] || [lower];
      return candidates.some(c => enabledModules && enabledModules.includes(c));
    };

    const filterItems = (items) => {
      return items.reduce((acc, item) => {
        const hasAccess = !item.permissions || item.permissions.length === 0 || hasAnyPermission(item.permissions);
        const isModuleEnabled = checkModuleEntitled(item.module);
        
        if (item.children) {
          const filteredChildren = filterItems(item.children);
          if (filteredChildren.length > 0 && isModuleEnabled) {
            acc.push({ ...item, children: filteredChildren });
          } else if (hasAccess && isModuleEnabled && item.href && item.href !== '#') {
             acc.push({ ...item, children: [] });
          }
        } else {
          if (hasAccess && isModuleEnabled) {
            acc.push(item);
          }
        }
        return acc;
      }, []);
    };
    const result = filterItems(activeNavigation);
    return result;
  }, [hasAnyPermission, activeNavigation, enabledModules, user, isSuperAdmin, supportSessionTenant])

  const userInitials = useMemo(() => {
    if (!user?.name) return 'SA'
    const parts = user.name.split(' ').filter(Boolean)
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    return user.name.slice(0, 2).toUpperCase()
  }, [user?.name])

  const handleMouseEnterItem = (name) => {
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    setHoveredMenu(name)
  }

  const handleMouseLeaveItem = () => {
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null)
    }, 280)
  }

  return (
    <>
      <SupportSessionBanner />
      <header className="relative z-[100] border-b border-slate-800 bg-[#0f172a] shadow-md w-full select-none" ref={navContainerRef}>
        <div className={clsx('text-white h-12 flex items-center justify-between shrink-0 w-full', fullWidth ? 'px-2 sm:px-4' : 'px-3 sm:px-4')}>
          
          {/* Left Section: Beautiful Brand Logo & Horizontal Nav Items */}
          <div className="flex items-center gap-1.5 lg:gap-2.5 min-w-0 flex-1 h-full overflow-visible">
            <button 
              onClick={onMobileMenuOpen}
              className="lg:hidden p-1.5 text-slate-300 hover:text-white rounded-md hover:bg-slate-800 transition-colors shrink-0"
              title="Open Navigation Drawer"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Brand Logo: Official Global AI ERP Logo fitted tightly to navbar height */}
            <Link 
              to={isSuperAdmin || user?.type === 'SuperAdmin' ? '/superadmin/control-tower' : (activePortal === 'vendor' ? '/vendor/dashboard' : '/dashboard')} 
              className="flex items-center h-full group shrink-0 pr-2.5 sm:pr-3 border-r border-slate-800/80 py-[0.5px]"
              title="Global AI ERP"
              onClick={() => setHoveredMenu(null)}
            >
              <BrandLogo 
                className="h-[40px] sm:h-[44px] max-h-[46px] w-auto object-contain transition-all group-hover:scale-[1.02]" 
                dark={true}
                showBySanglob={false}
              />
            </Link>

            {/* Main Menu Horizontal Items: Overflow visible so submenus and hierarchical dropdowns are unclipped */}
            <nav className="hidden sm:flex items-center space-x-0.5 sm:space-x-1 h-12 min-w-0 flex-1 overflow-visible py-1" aria-label="Global Navigation">
              {filteredNavigation.map((item) => {
                const isOpen = hoveredMenu === item.name
                const hasChildren = item.children && item.children.length > 0

                return (
                  <div 
                    key={item.name} 
                    className="relative h-full flex items-center shrink-0"
                    onMouseEnter={() => handleMouseEnterItem(item.name)}
                    onMouseOver={() => handleMouseEnterItem(item.name)}
                    onMouseLeave={handleMouseLeaveItem}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        onClick={() => setHoveredMenu(prev => prev === item.name ? null : item.name)}
                        className={clsx(
                          "flex items-center gap-1 px-2 sm:px-2.5 py-1 text-xs font-semibold rounded whitespace-nowrap transition-all duration-150 cursor-pointer select-none shrink-0",
                          isOpen
                            ? "bg-slate-800 text-white shadow-sm ring-1 ring-slate-700/60"
                            : "text-slate-200 hover:text-white hover:bg-slate-800/90"
                        )}
                      >
                        {item.icon && <item.icon className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-400 shrink-0" />}
                        <span>{item.name}</span>
                        <ChevronDown className={clsx("w-3 h-3 text-slate-400 transition-transform duration-200 shrink-0", isOpen ? "rotate-180 text-blue-400" : "")} />
                      </button>
                    ) : (
                      (() => {
                        const isItemActive = location.pathname === item.href || (item.href !== '/dashboard' && item.href !== '/' && location.pathname.startsWith(item.href))
                        return (
                          <Link
                            to={item.href}
                            onClick={() => setHoveredMenu(null)}
                            className={clsx(
                              "flex items-center gap-1.5 px-2 sm:px-2.5 py-1 text-xs font-semibold rounded whitespace-nowrap transition-colors shrink-0",
                              isItemActive
                                ? "bg-slate-800 text-white font-bold ring-1 ring-slate-700/80 shadow-sm"
                                : "text-slate-200 hover:text-white hover:bg-slate-800/90"
                            )}
                          >
                            {item.icon && (
                              <item.icon className={clsx("w-3.5 h-3.5 shrink-0", isItemActive ? "text-blue-400" : "text-slate-400")} />
                            )}
                            <span>{item.name}</span>
                          </Link>
                        )
                      })()
                    )}

                    {/* Hierarchical Submenu Dropdown */}
                    {hasChildren && (
                      <HierarchicalNavDropdown
                        item={item}
                        isOpen={isOpen}
                        onClose={() => setHoveredMenu(null)}
                      />
                    )}
                  </div>
                )
              })}
            </nav>
          </div>

        {/* Right Section: Real-time Live Notifications & Profile Menu */}
        <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0 pl-2">
          {/* Live Real-Time Notifications Popover */}
          <RealTimeNotificationCenter />

          {/* User Profile Dropdown */}
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen(prev => !prev)}
              className="flex items-center gap-1.5 p-0.5 rounded hover:bg-slate-800 transition-colors text-left cursor-pointer"
            >
              <div className="w-6 h-6 rounded bg-blue-600 text-white font-bold text-[11px] flex items-center justify-center shadow-inner shrink-0">
                {userInitials}
              </div>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 py-1.5 z-[99999] animate-in fade-in zoom-in-95 duration-100 text-slate-800">
                <div className="px-3.5 py-2 border-b border-slate-100">
                  <div className="text-xs font-bold text-slate-900 truncate">{user?.name || 'Administrator'}</div>
                  <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
                  <div className="mt-1 flex items-center gap-1">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                      {user?.type === 'SuperAdmin' ? 'SuperAdmin' : 'Tenant Admin'}
                    </span>
                  </div>
                </div>

                <div className="py-1">
                  {isSuperAdmin ? (
                    <>
                      <Link
                        to="/superadmin/control-tower"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-xs text-blue-700 hover:bg-blue-50 font-bold transition-colors"
                      >
                        <Layers className="w-3.5 h-3.5 text-blue-600" />
                        Admin Control Tower
                      </Link>
                      <Link
                        to="/superadmin/clients"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <Building2 className="w-3.5 h-3.5 text-indigo-600" />
                        Client Organizations
                      </Link>
                      <Link
                        to="/superadmin/licenses"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <Shield className="w-3.5 h-3.5 text-emerald-600" />
                        Cryptographic Licensing
                      </Link>
                      <Link
                        to="/superadmin/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5 text-slate-500" />
                        Platform Settings
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/settings/users"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <User className="w-3.5 h-3.5 text-indigo-600" />
                        User Management & Roles
                      </Link>
                      <Link
                        to="/settings/business-configuration"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <Building2 className="w-3.5 h-3.5 text-blue-600" />
                        Business Configuration
                      </Link>
                      <Link
                        to="/settings/system"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5 text-slate-500" />
                        System Settings
                      </Link>
                      <Link
                        to="/settings/license"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <Shield className="w-3.5 h-3.5 text-emerald-600" />
                        Enterprise License
                      </Link>
                    </>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false)
                      setShowSignOutModal(true)
                    }}
                    className="flex items-center gap-2 px-3.5 py-2 text-xs text-rose-600 hover:bg-rose-50 w-full text-left font-medium transition-colors cursor-pointer"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modern Executive Modal for Sign Out */}
      <SignOutModal
        isOpen={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={confirmSignOut}
        isSigningOut={isSigningOut}
      />
    </header>
    </>
  )
}
